import { useState, useRef, useEffect } from 'react';
import {
  MdClose,
  MdPictureAsPdf,
  MdDescription,
  MdCheckCircle,
  MdContentPaste,
  MdExpandMore,
  MdExpandLess,
  MdSchool,
  MdMenuBook,
  MdQuiz,
  MdTrendingUp,
  MdFullscreen,
  MdFullscreenExit,
} from 'react-icons/md';
import { 
  parseCurriculumDocument, 
  extractTextFromWordInBrowser, 
  extractTextFromPdfInBrowser 
} from '../../services/aiService';
import { doc, collection, setDoc, serverTimestamp } from 'firebase/firestore';
import { db } from '../../services/firebase';
import { saveCurriculumTopic, saveCurriculumQuestion } from '../../services/curriculumEditorService';
import { getSubjectName, getSubjectClass } from '../../utils/constants';

const AIDocumentImporterModal = ({
  isOpen,
  onClose,
  initialClass = 'ss1',
  initialSubject = '',
  initialTerm = 'Term 1',
  userProfile = {},
  isAdmin = false,
  onImportComplete = () => {},
}) => {
  const [selectedClass, setSelectedClass] = useState(initialClass);
  const [selectedSubject, setSelectedSubject] = useState(initialSubject);
  const [selectedTerm, setSelectedTerm] = useState(initialTerm);

  useEffect(() => {
    if (isOpen) {
      setSelectedClass(initialClass);
      setSelectedSubject(initialSubject);
      setSelectedTerm(initialTerm);
      setErrorMsg('');
      setPublishSuccessMsg('');
    }
  }, [isOpen, initialClass, initialSubject, initialTerm]);

  const [inputMode, setInputMode] = useState('file'); // 'file' or 'text'
  const [file, setFile] = useState(null);
  const [pastedText, setPastedText] = useState('');

  const [isAnalyzing, setIsAnalyzing] = useState(false);
  const [analysisStep, setAnalysisStep] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  // Extracted Result State
  const [extractedData, setExtractedData] = useState(null);
  const [selectedTopicIndices, setSelectedTopicIndices] = useState(new Set());
  const [expandedTopicIndex, setExpandedTopicIndex] = useState(0);

  const [isPublishing, setIsPublishing] = useState(false);
  const [publishSuccessMsg, setPublishSuccessMsg] = useState('');
  const [isFullScreen, setIsFullScreen] = useState(true);
  const fileInputRef = useRef(null);

  if (!isOpen) return null;

  const fileToBase64 = (fileObj) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsDataURL(fileObj);
      reader.onload = () => {
        const result = reader.result;
        const base64 = typeof result === 'string' && result.includes(',') ? result.split(',')[1] : result;
        resolve(base64);
      };
      reader.onerror = reject;
    });
  };

  const fileToText = (fileObj) => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.readAsText(fileObj);
      reader.onload = () => resolve(reader.result);
      reader.onerror = reject;
    });
  };

  const handleFileDrop = (e) => {
    e.preventDefault();
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      setFile(e.dataTransfer.files[0]);
    }
  };

  const handleAnalyze = async () => {
    setErrorMsg('');
    setPublishSuccessMsg('');

    if (inputMode === 'file' && !file) {
      setErrorMsg('Please select a PDF, Word, or Text document to import.');
      return;
    }
    if (inputMode === 'text' && !pastedText.trim()) {
      setErrorMsg('Please paste your syllabus or lesson text in the box below.');
      return;
    }

    setIsAnalyzing(true);
    setAnalysisStep('Reading document content...');

    try {
      let textPayload = '';
      let base64PdfPayload = null;

      if (inputMode === 'file' && file) {
        const fileName = (file.name || '').toLowerCase();
        const isPdf = file.type === 'application/pdf' || fileName.endsWith('.pdf');
        const isWord = fileName.endsWith('.doc') || fileName.endsWith('.docx') || file.type.includes('word') || file.type.includes('officedocument');

        if (isWord) {
          setAnalysisStep('Extracting Word document text...');
          const docResult = await extractTextFromWordInBrowser(file);
          textPayload = docResult.text || '';
        } else if (isPdf) {
          setAnalysisStep('Parsing PDF pages & layout...');
          try {
            const pdfResult = await extractTextFromPdfInBrowser(file);
            if (pdfResult && pdfResult.text && pdfResult.text.length > 50) {
              textPayload = pdfResult.text;
            } else {
              base64PdfPayload = await fileToBase64(file);
            }
          } catch (_) {
            base64PdfPayload = await fileToBase64(file);
          }
        } else {
          setAnalysisStep('Reading document text...');
          textPayload = await fileToText(file);
        }
      } else {
        textPayload = pastedText.trim();
      }

      if (!textPayload.trim() && !base64PdfPayload) {
        throw new Error('No readable text could be extracted from this file. Please copy and paste your notes directly into the "Paste Text" tab.');
      }

      setAnalysisStep('AI is structuring syllabus roadmap & lessons...');

      const result = await parseCurriculumDocument({
        text: textPayload,
        base64Pdf: base64PdfPayload,
        classLevel: selectedClass,
        subject: getSubjectName(selectedSubject) || selectedSubject,
        term: selectedTerm,
      });

      if (!result || !result.topics || !Array.isArray(result.topics) || result.topics.length === 0) {
        throw new Error('Could not identify curriculum topics from this document. Please verify the content format.');
      }

      setExtractedData(result);
      // Select all detected topics by default
      setSelectedTopicIndices(new Set(result.topics.map((_, i) => i)));
      setExpandedTopicIndex(0);
    } catch (err) {
      console.error('Document analysis error:', err);
      setErrorMsg(err.message || 'Failed to analyze document. Please check your network and try again.');
    } finally {
      setIsAnalyzing(false);
      setAnalysisStep('');
    }
  };

  const toggleTopicSelect = (index) => {
    const next = new Set(selectedTopicIndices);
    if (next.has(index)) {
      next.delete(index);
    } else {
      next.add(index);
    }
    setSelectedTopicIndices(next);
  };

  const handlePublishAll = async () => {
    if (!extractedData || selectedTopicIndices.size === 0) {
      setErrorMsg('Please select at least one topic to import.');
      return;
    }

    setIsPublishing(true);
    setErrorMsg('');
    setPublishSuccessMsg('');

    try {
      const targetClass = selectedClass || extractedData.classLevel?.toLowerCase() || 'ss1';
      const targetSub = selectedSubject || 'mathematics';
      const targetSubName = getSubjectName(targetSub);
      const teacherUid = userProfile?.uid || 'admin_user';
      const teacherName = userProfile?.fullName || (isAdmin ? 'Admin' : 'Teacher');

      let savedTopicCount = 0;
      let savedLessonCount = 0;
      let savedQuestionsCount = 0;

      for (const idx of selectedTopicIndices) {
        const t = extractedData.topics[idx];
        if (!t) continue;

        const effectiveTerm = t.term || selectedTerm || 'Term 1';

        // 1. Save Topic to content_approvals collection (for upload page)
        const topicRef = doc(collection(db, 'content_approvals'));
        const topicId = topicRef.id;

        await setDoc(topicRef, {
          id: topicId,
          title: t.title?.trim() || `Topic ${idx + 1}`,
          type: 'Topic',
          subjectId: targetSub,
          subjectName: targetSubName,
          subject: targetSubName,
          classLevel: targetClass,
          term: effectiveTerm,
          status: 'approved',
          order: idx + 1,
          subtopics: t.subtopics || [],
          learningObjectives: t.learningObjectives || [],
          teacherId: teacherUid,
          teacherName,
          createdAt: serverTimestamp(),
        });
        savedTopicCount++;

        // 2. Also register in curriculum collection so /curriculum syncs instantly
        try {
          await saveCurriculumTopic({
            topicId,
            classLevel: targetClass,
            subjectId: targetSub,
            term: effectiveTerm,
            title: t.title?.trim(),
            order: idx + 1,
            subtopics: t.subtopics || [],
            learningObjectives: t.learningObjectives || [],
          });
        } catch (err) {
          console.warn('Could not mirror to curriculum collection:', err);
        }

        // 3. Save Lesson Notes to content_approvals (type: 'Lesson') to ground the AI Teacher
        if (t.lessonNotes) {
          const lessonRef = doc(collection(db, 'content_approvals'));
          const lessonBody = t.analogies
            ? `${t.lessonNotes}\n\n### Everyday Analogy\n${t.analogies}`
            : t.lessonNotes;

          await setDoc(lessonRef, {
            id: lessonRef.id,
            title: `${t.title?.trim()} - Interactive Lesson`,
            type: 'Lesson',
            subjectId: targetSub,
            subjectName: targetSubName,
            classLevel: getSubjectClass(targetSub),
            teacherId: teacherUid,
            teacherName,
            status: 'approved',
            createdAt: serverTimestamp(),
            contentData: {
              topicId,
              body: lessonBody,
              term: effectiveTerm,
              contentType: 'text',
              subtopics: t.subtopics || [],
              learningObjectives: t.learningObjectives || [],
            },
          });
          savedLessonCount++;
        }

        // 4. Save review questions to curriculum_questions (for quiz engine)
        if (t.practiceQuestions && Array.isArray(t.practiceQuestions)) {
          for (const q of t.practiceQuestions) {
            if (q.question && Array.isArray(q.options) && q.options.length >= 2) {
              await saveCurriculumQuestion({
                topicId,
                subjectId: targetSub,
                classLevel: targetClass,
                term: effectiveTerm,
                question: q.question,
                options: q.options,
                correctIndex: typeof q.correctOptionIndex === 'number' ? q.correctOptionIndex : 0,
                explanation: q.explanation || '',
              });
              savedQuestionsCount++;
            }
          }
        }
      }

      setPublishSuccessMsg(
        `Successfully published ${savedTopicCount} topic(s), ${savedLessonCount} lesson(s), and ${savedQuestionsCount} quiz question(s)! Your AI Tutor is now fully grounded with this material.`
      );

      onImportComplete();
      setTimeout(() => {
        onClose();
      }, 2500);
    } catch (err) {
      console.error('Publishing error:', err);
      setErrorMsg('Failed to publish imported curriculum: ' + err.message);
    } finally {
      setIsPublishing(false);
    }
  };

  return (
    <div className={`fixed inset-0 z-50 bg-slate-900/80 backdrop-blur-xs flex items-center justify-center ${isFullScreen ? 'p-0' : 'p-3 sm:p-5'} overflow-hidden animate-fadeIn`}>
      <div className={`bg-white shadow-2xl border border-slate-200 overflow-hidden flex flex-col transition-all duration-200 ${
        isFullScreen 
          ? 'w-screen h-screen rounded-none' 
          : 'w-full max-w-5xl rounded-2xl max-h-[92vh]'
      }`}>
        {/* Modal Header */}
        <div className="p-4 sm:p-5 border-b border-slate-100 flex items-center justify-between bg-slate-50/70 shrink-0">
          <div className="flex items-center gap-3">
            <div className="w-10 h-10 rounded-xl bg-indigo-600 text-white flex items-center justify-center shadow-sm shrink-0">
              <MdSchool className="text-xl" />
            </div>
            <div>
              <h3 className="text-lg font-bold text-slate-900 font-poppins">
                AI Document & Curriculum Importer
              </h3>
              <p className="text-xs text-slate-500">
                Upload a syllabus, scheme of work, or textbook notes to automatically generate lessons and ground the AI Tutor.
              </p>
            </div>
          </div>
          <div className="flex items-center gap-1.5">
            <button
              type="button"
              onClick={() => setIsFullScreen(!isFullScreen)}
              className="p-2 text-slate-500 hover:text-slate-800 rounded-lg hover:bg-slate-200/70 transition-colors cursor-pointer"
              title={isFullScreen ? 'Restore Down' : 'Full Screen'}
            >
              {isFullScreen ? <MdFullscreenExit size={22} /> : <MdFullscreen size={22} />}
            </button>
            <button
              type="button"
              onClick={onClose}
              className="p-2 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-200/70 transition-colors cursor-pointer"
              title="Close"
            >
              <MdClose size={22} />
            </button>
          </div>
        </div>

        {/* Modal Body */}
        <div className="p-6 overflow-y-auto space-y-5 flex-1">
          {errorMsg && (
            <div className="p-3.5 bg-rose-50 border border-rose-200 text-rose-700 rounded-xl text-xs font-semibold">
              {errorMsg}
            </div>
          )}

          {publishSuccessMsg && (
            <div className="p-3.5 bg-green-50 border border-green-200 text-green-800 rounded-xl text-xs font-semibold flex items-center gap-2">
              <MdCheckCircle className="text-lg text-green-600 shrink-0" />
              <span>{publishSuccessMsg}</span>
            </div>
          )}

          {!extractedData ? (
            /* STEP 1: Upload and Configuration */
            <div className="space-y-4 max-w-4xl mx-auto w-full py-2">
              {/* Context Selector Grid */}
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Class Level
                  </label>
                  <select
                    value={selectedClass}
                    onChange={(e) => setSelectedClass(e.target.value)}
                    className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-medium"
                  >
                    <option value="ss1">SS1</option>
                    <option value="ss2">SS2</option>
                    <option value="ss3">SS3</option>
                    <option value="jss1">JSS1</option>
                    <option value="jss2">JSS2</option>
                    <option value="jss3">JSS3</option>
                    <option value="utme">UTME / JAMB</option>
                  </select>
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Subject
                  </label>
                  <input
                    type="text"
                    value={getSubjectName(selectedSubject) || selectedSubject}
                    readOnly
                    className="w-full text-xs p-2.5 bg-slate-100 border border-slate-200 rounded-xl text-slate-700 font-semibold cursor-not-allowed"
                  />
                </div>

                <div>
                  <label className="block text-xs font-semibold text-slate-700 mb-1">
                    Academic Term
                  </label>
                  <select
                    value={selectedTerm}
                    onChange={(e) => setSelectedTerm(e.target.value)}
                    className="w-full text-xs p-2.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-medium"
                  >
                    <option value="Term 1">1st Term</option>
                    <option value="Term 2">2nd Term</option>
                    <option value="Term 3">3rd Term</option>
                  </select>
                </div>
              </div>

              {/* Mode Toggle */}
              <div className="flex items-center gap-2 border-b border-slate-100 pb-2 pt-1">
                <button
                  type="button"
                  onClick={() => setInputMode('file')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    inputMode === 'file'
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <MdPictureAsPdf className="text-sm" />
                  <span>Upload Document (PDF, Word, TXT)</span>
                </button>
                <button
                  type="button"
                  onClick={() => setInputMode('text')}
                  className={`inline-flex items-center gap-1.5 px-3 py-1.5 text-xs font-semibold rounded-lg transition-all ${
                    inputMode === 'text'
                      ? 'bg-indigo-50 text-indigo-700 border border-indigo-200'
                      : 'text-slate-600 hover:text-slate-900'
                  }`}
                >
                  <MdContentPaste className="text-sm" />
                  <span>Paste Text Directly</span>
                </button>
              </div>

              {inputMode === 'file' ? (
                /* Drag and drop zone */
                <div
                  onDragOver={(e) => e.preventDefault()}
                  onDrop={handleFileDrop}
                  onClick={() => fileInputRef.current?.click()}
                  className="border-2 border-dashed border-slate-300 hover:border-indigo-500 rounded-2xl p-8 text-center cursor-pointer transition-all bg-slate-50/50 hover:bg-indigo-50/20 group"
                >
                  <input
                    ref={fileInputRef}
                    type="file"
                    accept=".pdf,.doc,.docx,.txt,.md"
                    className="hidden"
                    onChange={(e) => {
                      if (e.target.files && e.target.files.length > 0) {
                        setFile(e.target.files[0]);
                      }
                    }}
                  />
                  <div className="w-12 h-12 mx-auto rounded-2xl bg-indigo-50 text-indigo-600 flex items-center justify-center mb-3 group-hover:scale-110 transition-transform">
                    {file ? (
                      <MdCheckCircle className="text-2xl text-green-600" />
                    ) : (
                      <MdDescription className="text-2xl" />
                    )}
                  </div>
                  {file ? (
                    <div>
                      <p className="text-sm font-bold text-slate-900">{file.name}</p>
                      <p className="text-xs text-slate-500 mt-1">
                        {(file.size / (1024 * 1024)).toFixed(2)} MB • Ready to analyze
                      </p>
                    </div>
                  ) : (
                    <div>
                      <p className="text-sm font-bold text-slate-800">
                        Click to upload or drag & drop your document
                      </p>
                      <p className="text-xs text-slate-400 mt-1">
                        Supported files: PDF, DOCX, TXT, Markdown (Max 15MB)
                      </p>
                    </div>
                  )}
                </div>
              ) : (
                /* Text Area */
                <div>
                  <textarea
                    rows={8}
                    value={pastedText}
                    onChange={(e) => setPastedText(e.target.value)}
                    placeholder="Paste your curriculum syllabus, chapter summary, or lesson outline here..."
                    className="w-full text-xs p-3.5 bg-slate-50 border border-slate-200 rounded-xl focus:ring-2 focus:ring-indigo-500 focus:outline-none font-sans"
                  />
                </div>
              )}

              {/* Action Button */}
              <button
                type="button"
                onClick={handleAnalyze}
                disabled={isAnalyzing}
                className="w-full py-3 rounded-xl bg-indigo-600 hover:bg-indigo-700 text-white font-bold text-xs shadow-md shadow-indigo-600/20 transition-all flex items-center justify-center gap-2 disabled:opacity-50"
              >
                {isAnalyzing ? (
                  <>
                    <span className="w-4 h-4 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                    <span>{analysisStep || 'Analyzing Document...'}</span>
                  </>
                ) : (
                  <>
                    <MdTrendingUp className="text-base" />
                    <span>Extract Curriculum & Generate Lessons</span>
                  </>
                )}
              </button>
            </div>
          ) : (
            /* STEP 2: Interactive Staging & Review */
            <div className="space-y-4">
              {/* Summary Bar */}
              <div className="p-3.5 bg-slate-50 border border-slate-200 rounded-xl flex flex-col sm:flex-row sm:items-center justify-between gap-2.5">
                <div>
                  <div className="flex items-center gap-2">
                    <span className="text-xs font-bold text-slate-900">
                      Found {extractedData.topics.length} topic(s) in {extractedData.subject || getSubjectName(selectedSubject)}
                    </span>
                    {extractedData.topics.reduce((acc, t) => acc + (t.subtopics?.length || 0), 0) > 0 && (
                      <span className="text-[10px] font-bold px-2 py-0.5 rounded-full bg-indigo-100 text-indigo-700">
                        {extractedData.topics.reduce((acc, t) => acc + (t.subtopics?.length || 0), 0)} subtopics
                      </span>
                    )}
                  </div>
                  <p className="text-[11px] text-slate-500 mt-0.5">
                    Review each topic, its subtopics, and quiz exercises before saving to your curriculum.
                  </p>
                </div>
                <div className="flex items-center gap-2 shrink-0">
                  <button
                    type="button"
                    onClick={() => {
                      if (selectedTopicIndices.size === extractedData.topics.length) {
                        setSelectedTopicIndices(new Set());
                      } else {
                        setSelectedTopicIndices(new Set(extractedData.topics.map((_, i) => i)));
                      }
                    }}
                    className="text-xs px-2.5 py-1 rounded-lg bg-white border border-slate-200 text-slate-700 hover:bg-slate-50 font-semibold transition-colors"
                  >
                    {selectedTopicIndices.size === extractedData.topics.length ? 'Deselect All' : 'Select All'}
                  </button>
                  <button
                    type="button"
                    onClick={() => {
                      setExtractedData(null);
                      setFile(null);
                      setPastedText('');
                    }}
                    className="text-xs text-slate-500 hover:text-slate-800 underline font-medium"
                  >
                    Upload Another
                  </button>
                </div>
              </div>

              {/* Topics List with Preview */}
              <div className="space-y-3">
                {extractedData.topics.map((t, idx) => {
                  const isChecked = selectedTopicIndices.has(idx);
                  const isExpanded = expandedTopicIndex === idx;

                  return (
                    <div
                      key={idx}
                      className={`border rounded-xl transition-all ${
                        isChecked ? 'border-indigo-200 bg-white shadow-2xs' : 'border-slate-200 bg-slate-50/50 opacity-60'
                      }`}
                    >
                      <div className="p-3.5 flex items-start justify-between gap-3">
                        <div className="flex items-start gap-3 flex-1 min-w-0">
                          <input
                            type="checkbox"
                            checked={isChecked}
                            onChange={() => toggleTopicSelect(idx)}
                            className="w-4 h-4 text-indigo-600 rounded-sm focus:ring-indigo-500 mt-0.5"
                          />
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="text-[10px] font-bold px-2.5 py-0.5 rounded-full bg-indigo-50 text-indigo-700 border border-indigo-100 shrink-0">
                                Lesson {t.lessonNumber || t.week || idx + 1}:
                              </span>
                              <span className="text-xs font-bold text-slate-900">{t.title}</span>
                            </div>
                            <p className="text-[11px] text-slate-500 mt-0.5">
                              {t.subtopics?.length ? `${t.subtopics.length} subtopic(s) • ` : ''}
                              {t.learningObjectives?.length || 0} objective(s) •{' '}
                              {t.practiceQuestions?.length || 0} exercise(s)
                            </p>

                            {/* Subtopics Pills */}
                            {t.subtopics && t.subtopics.length > 0 && (
                              <div className="flex flex-wrap gap-1.5 mt-2">
                                {t.subtopics.map((sub, sIdx) => (
                                  <span
                                    key={sIdx}
                                    className="inline-flex items-center gap-1 text-[10px] font-medium px-2 py-0.5 rounded-md bg-indigo-50/80 text-indigo-700 border border-indigo-100/80"
                                  >
                                    <span className="w-1.5 h-1.5 rounded-full bg-indigo-500 shrink-0" />
                                    {sub}
                                  </span>
                                ))}
                              </div>
                            )}
                          </div>
                        </div>

                        <button
                          type="button"
                          onClick={() => setExpandedTopicIndex(isExpanded ? -1 : idx)}
                          className="p-1.5 text-slate-400 hover:text-slate-700 rounded-lg hover:bg-slate-100 shrink-0"
                          title={isExpanded ? 'Collapse' : 'Expand'}
                        >
                          {isExpanded ? <MdExpandLess className="text-lg" /> : <MdExpandMore className="text-lg" />}
                        </button>
                      </div>

                      {/* Expandable Preview */}
                      {isExpanded && (
                        <div className="p-4 border-t border-slate-100 bg-slate-50/40 space-y-3.5 text-xs">
                          {/* Subtopics Detailed Breakdown */}
                          {t.subtopics && t.subtopics.length > 0 && (
                            <div>
                              <span className="font-semibold text-slate-700 flex items-center gap-1 mb-1.5">
                                <MdSchool className="text-sm text-indigo-600" />
                                Subtopics & Key Concepts:
                              </span>
                              <div className="grid grid-cols-1 sm:grid-cols-2 gap-1.5">
                                {t.subtopics.map((sub, i) => (
                                  <div
                                    key={i}
                                    className="p-2 bg-white border border-slate-200 rounded-lg text-[11px] text-slate-700 flex items-center gap-2"
                                  >
                                    <span className="w-4 h-4 rounded-full bg-indigo-100 text-indigo-700 flex items-center justify-center font-bold text-[9px] shrink-0">
                                      {i + 1}
                                    </span>
                                    <span>{sub}</span>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}

                          {t.learningObjectives && t.learningObjectives.length > 0 && (
                            <div>
                              <span className="font-semibold text-slate-700 flex items-center gap-1 mb-1">
                                <MdSchool className="text-sm text-indigo-600" />
                                Learning Objectives:
                              </span>
                              <ul className="list-disc pl-5 text-slate-600 space-y-0.5 text-[11px]">
                                {t.learningObjectives.map((obj, i) => (
                                  <li key={i}>{obj}</li>
                                ))}
                              </ul>
                            </div>
                          )}

                          {t.analogies && (
                            <div className="p-2.5 bg-amber-50/80 border border-amber-200/70 rounded-lg text-[11px] text-amber-900">
                              <span className="font-bold flex items-center gap-1 text-amber-800 mb-0.5">
                                💡 Everyday Real-Life Analogy:
                              </span>
                              {t.analogies}
                            </div>
                          )}

                          {t.lessonNotes && (
                            <div>
                              <span className="font-semibold text-slate-700 flex items-center gap-1 mb-1">
                                <MdMenuBook className="text-sm text-indigo-600" />
                                Generated Lesson Content:
                              </span>
                              <div className="p-2.5 bg-white border border-slate-200 rounded-lg text-slate-700 text-[11px] leading-relaxed max-h-36 overflow-y-auto">
                                {t.lessonNotes}
                              </div>
                            </div>
                          )}

                          {t.practiceQuestions && t.practiceQuestions.length > 0 && (
                            <div>
                              <span className="font-semibold text-slate-700 flex items-center gap-1 mb-1">
                                <MdQuiz className="text-sm text-indigo-600" />
                                Extracted Exercises ({t.practiceQuestions.length}):
                              </span>
                              <div className="space-y-1.5">
                                {t.practiceQuestions.map((pq, qIdx) => (
                                  <div
                                    key={qIdx}
                                    className="p-2 bg-white border border-slate-200 rounded-lg text-[11px]"
                                  >
                                    <p className="font-medium text-slate-800">{pq.question}</p>
                                    <div className="grid grid-cols-2 gap-1 mt-1 text-[10px] text-slate-500">
                                      {pq.options?.map((opt, oIdx) => (
                                        <span
                                          key={oIdx}
                                          className={`px-2 py-0.5 rounded ${
                                            oIdx === pq.correctOptionIndex
                                              ? 'bg-green-50 text-green-700 font-bold border border-green-200'
                                              : 'bg-slate-50'
                                          }`}
                                        >
                                          {String.fromCharCode(65 + oIdx)}. {opt}
                                        </span>
                                      ))}
                                    </div>
                                  </div>
                                ))}
                              </div>
                            </div>
                          )}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
            </div>
          )}
        </div>

        {/* Modal Footer - Always clearly visible and docked at the bottom */}
        {extractedData && (
          <div className="p-4 px-6 border-t border-slate-200 bg-slate-50 flex items-center justify-between rounded-b-2xl">
            <span className="text-xs text-slate-700 font-bold">
              {selectedTopicIndices.size} of {extractedData.topics.length} topic(s) selected
            </span>
            <button
              type="button"
              onClick={handlePublishAll}
              disabled={isPublishing || selectedTopicIndices.size === 0}
              className="px-6 py-2.5 rounded-xl text-white font-bold text-xs shadow-md shadow-green-600/30 transition-all flex items-center gap-2 active:scale-95 disabled:opacity-50 disabled:cursor-not-allowed cursor-pointer"
              style={{ backgroundColor: '#16a34a', color: '#ffffff' }}
            >
              {isPublishing ? (
                <>
                  <span className="w-3.5 h-3.5 border-2 border-white/30 border-t-white rounded-full animate-spin" />
                  <span>Publishing to AI Tutor...</span>
                </>
              ) : (
                <>
                  <MdCheckCircle className="text-base text-white" />
                  <span>Save & Ground AI Tutor ({selectedTopicIndices.size})</span>
                </>
              )}
            </button>
          </div>
        )}
      </div>
    </div>
  );
};

export default AIDocumentImporterModal;
